import { useState, useEffect } from 'react';
import Link from 'next/link';
import MobileBackButton from '../../components/MobileBackButton';

export default function APIVersioning() {
  const [apiStatus, setApiStatus] = useState(null);

  useEffect(() => {
    // Fetch API status for current version info
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
    fetch(`${apiUrl}/v1/users/status/`)
      .then(res => res.json())
      .then(data => setApiStatus(data))
      .catch(err => console.error('Failed to fetch API status:', err));
  }, []);

  return (
    <div className="flex-1 p-8 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center mb-6">
          <MobileBackButton />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">API Versioning & Stability Policy</h1>
        </div>

        {/* Summary Card */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-6 mb-8 rounded-r-lg">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-blue-600 dark:text-blue-400 mr-3 mt-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <h2 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-2">
                Semantic Versioning with Extended Support
              </h2>
              <p className="text-blue-800 dark:text-blue-300">
                We use semantic versioning for all API changes. Breaking changes require new major versions (e.g., v1 → v2).
                We support 2 major versions simultaneously, giving you a 4-month migration window.
              </p>
            </div>
          </div>
        </div>

        {/* Current Version */}
        {apiStatus && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Current API Version</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Production environment status</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-1">
                  v{apiStatus.api_contract_version}
                </div>
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-xs font-medium">
                  {apiStatus.status}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Semantic Versioning */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-8 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">How We Version Our API</h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Semantic Versioning Format</h3>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 font-mono text-center mb-4">
                <span className="text-2xl font-bold text-gray-900 dark:text-white">
                  <span className="text-red-600 dark:text-red-400">MAJOR</span>.
                  <span className="text-yellow-600 dark:text-yellow-400">MINOR</span>.
                  <span className="text-green-600 dark:text-green-400">PATCH</span>
                </span>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="border-l-4 border-red-500 pl-4">
                  <div className="font-semibold text-red-600 dark:text-red-400 mb-1">MAJOR</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Breaking changes that require code updates</div>
                </div>
                <div className="border-l-4 border-yellow-500 pl-4">
                  <div className="font-semibold text-yellow-600 dark:text-yellow-400 mb-1">MINOR</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">New features, backwards compatible</div>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <div className="font-semibold text-green-600 dark:text-green-400 mb-1">PATCH</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Bug fixes, backwards compatible</div>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Breaking Changes = New Major Version</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-3">
                When we need to introduce breaking changes, we release a new major version (e.g., v2.0.0).
                The old version continues to work alongside the new version during the migration period.
              </p>
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                <p className="text-sm text-blue-900 dark:text-blue-200">
                  <strong>Key principle:</strong> You never wake up to broken API calls. Old versions remain supported
                  through a defined deprecation period, giving you time to migrate on your schedule.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Version Lifecycle */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-8 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Version Lifecycle</h2>

          <div className="space-y-6">
            <div className="flex items-start">
              <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 font-bold mr-4">
                1
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Staging Release (30 Days)</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-2">
                  New versions are first released to our staging environment (<code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">staging-api.creolecentric.com</code>).
                  This gives you a full month to test the new version with your code before it reaches production.
                </p>
                <div className="bg-gray-50 dark:bg-gray-900 rounded p-3 text-sm">
                  <strong className="text-gray-900 dark:text-white">During this phase:</strong>
                  <ul className="mt-2 space-y-1 text-gray-600 dark:text-gray-400 ml-4 list-disc">
                    <li>Full documentation and migration guides published</li>
                    <li>Email notification sent to all API key holders</li>
                    <li>Developer support available for migration questions</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-400 font-bold mr-4">
                2
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Production Deployment (15 Days Dual Support)</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-2">
                  The new version is deployed to production. Both the old and new versions are active and fully supported.
                  You can migrate when ready during this overlap period.
                </p>
                <div className="bg-gray-50 dark:bg-gray-900 rounded p-3 text-sm">
                  <strong className="text-gray-900 dark:text-white">During this phase:</strong>
                  <ul className="mt-2 space-y-1 text-gray-600 dark:text-gray-400 ml-4 list-disc">
                    <li>Both versions receive full support and bug fixes</li>
                    <li>You can switch between versions by changing the API endpoint</li>
                    <li>Final reminder email sent 7 days before deprecation begins</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400 font-bold mr-4">
                3
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Deprecation Period (3 Months)</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-2">
                  The old version is marked as deprecated but continues to function normally. This gives you 3 months to complete your migration.
                </p>
                <div className="bg-gray-50 dark:bg-gray-900 rounded p-3 text-sm">
                  <strong className="text-gray-900 dark:text-white">During this phase:</strong>
                  <ul className="mt-2 space-y-1 text-gray-600 dark:text-gray-400 ml-4 list-disc">
                    <li>Old version continues to work without interruption</li>
                    <li>Critical security fixes applied to deprecated version</li>
                    <li>Monthly reminder emails about upcoming sunset</li>
                    <li>New features only added to current version</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400 font-bold mr-4">
                4
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Version Sunset</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-2">
                  After 4+ months total (30 days staging + 15 days overlap + 3 months deprecation), the old version is retired.
                  API calls to the old version will return a 410 Gone status with migration instructions.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-green-600 dark:text-green-400 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div className="text-sm">
                  <strong className="text-green-900 dark:text-green-200">Total Migration Window: 4+ months</strong>
                  <p className="text-green-800 dark:text-green-300 mt-1">
                    From the moment a new version is announced in staging, you have over 4 months to migrate before the old version sunsets.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* What Constitutes Breaking vs Non-Breaking */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-8 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Breaking vs. Non-Breaking Changes</h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                Breaking Changes (Require New Major Version)
              </h3>
              <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Removing or renaming endpoints</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Removing or renaming request/response fields</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Changing field types (string → number, etc.)</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Making optional fields required</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Changing authentication methods</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Changing error response formats</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Changing rate limit behavior</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-green-600 dark:text-green-400 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Non-Breaking Changes (Same Version)
              </h3>
              <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Adding new endpoints</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Adding optional request parameters</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Adding new response fields</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Making required fields optional</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Bug fixes that restore documented behavior</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Performance improvements</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Documentation updates</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* API Environments */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-8 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">API Environments</h2>

          <div className="space-y-4">
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Production</h3>
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-xs font-medium">
                  Live
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center text-sm">
                  <span className="text-gray-600 dark:text-gray-400 mr-2">Base URL:</span>
                  <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-gray-900 dark:text-gray-100">
                    https://api.creolecentric.com/v1
                  </code>
                </div>
                {apiStatus && (
                  <div className="flex items-center text-sm">
                    <span className="text-gray-600 dark:text-gray-400 mr-2">Current Version:</span>
                    <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-gray-900 dark:text-gray-100">
                      v{apiStatus.api_contract_version}
                    </code>
                  </div>
                )}
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Stable production environment for all live applications.
                </p>
              </div>
            </div>

            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Staging</h3>
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs font-medium">
                  Coming Soon
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center text-sm">
                  <span className="text-gray-600 dark:text-gray-400 mr-2">Base URL:</span>
                  <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-gray-900 dark:text-gray-100">
                    https://staging-api.creolecentric.com/v1
                  </code>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Pre-production environment for testing new API versions before they go live.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stay Informed */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Stay Informed About Version Changes</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            We'll keep you updated every step of the way:
          </p>
          <div className="space-y-3">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
              <div>
                <div className="font-medium text-gray-900 dark:text-white">Email Notifications</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Sent to all API key holders when new versions are announced, 7 days before deprecation, and monthly during deprecation period
                </div>
              </div>
            </div>
            <div className="flex items-start">
              <svg className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <Link href="/developer/changelog" className="font-medium text-blue-600 dark:text-blue-400 hover:underline">
                  API Changelog
                </Link>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Detailed documentation of all API changes with migration guides
                </div>
              </div>
            </div>
            <div className="flex items-start">
              <svg className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
              </svg>
              <div>
                <div className="font-medium text-gray-900 dark:text-white">Response Headers</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Deprecated endpoints include <code className="bg-white dark:bg-gray-700 px-1 rounded text-xs">Sunset</code> and <code className="bg-white dark:bg-gray-700 px-1 rounded text-xs">Deprecation</code> headers
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Back to Developer Portal */}
        <div className="text-center mt-8">
          <Link
            href="/developer"
            className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline"
          >
            ← Back to Developer Portal
          </Link>
        </div>
      </div>
    </div>
  );
}
